import { useEffect, useMemo, useRef, useState } from 'react'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { toast } from 'sonner'
import { Clock, MapPinOff } from 'lucide-react'

import { formatDistancePl } from '@czas/shared'

import Button from '@/components/ui/Button'
import Label from '@/components/ui/Label'
import { ApiHttpError, apiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'

import { useResolveRemoteEnd, type WorkSession } from './use-work-sessions'

const ERROR_ID = 'remote-end-error'

/** Przez tyle od kliknięcia nagłówek mówi „Jesteś…"; później już tylko, gdzie kliknięto. */
const RECENT_CLICK_MS = 10 * 60_000

function timeValue(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

// Poniżej 10 km pełne metry, nie formatDistancePl: właściciel chciał dokładnej odległości, a zaokrąglone „1,3 km" czytało się jak mglista sugestia.
function exactDistanceLabel(meters: number): string {
  const rounded = Math.round(meters)
  if (rounded >= 0 && rounded < 10_000) return `${rounded} m`
  return formatDistancePl(meters)
}

/**
 * Modal „kończysz pracę poza placówką".
 *
 * Pracownik kliknął „Koniec pracy" daleko od kliniki (w tramwaju, w domu), więc
 * zapisana godzina zawyża jego czas pracy. Podaje właściwą godzinę wyjścia albo
 * potwierdza godzinę kliknięcia. Przy korekcie backend dopisuje audit log
 * i wysyła push do adminów.
 *
 * Korekta idzie wyłącznie W DÓŁ — moment kliknięcia jest górną granicą. Czasu,
 * którego system nie widział, pracownik sobie nie dopisze; od tego jest admin.
 */
export default function RemoteEndModal({
  session,
  onResolved,
  onDismiss,
}: {
  session: WorkSession
  /** Zapisano — lista odświeży się sama i pokaże ewentualną kolejną sesję. */
  onResolved: () => void
  /** „Zrobię to później" — modal znika, przypomnienie zostaje na ekranie. */
  onDismiss: () => void
}) {
  const resolve = useResolveRemoteEnd()

  const startedAt = useMemo(() => new Date(session.startedAt), [session.startedAt])
  const promptedAt = useMemo(
    () => new Date(session.remoteEndPromptAt ?? session.endedAt ?? session.startedAt),
    [session.remoteEndPromptAt, session.endedAt, session.startedAt],
  )
  const distanceLabel =
    session.remoteEndPromptDistanceM != null
      ? exactDistanceLabel(session.remoteEndPromptDistanceM)
      : null
  // Pytanie żyje 14 dni — po powrocie z banera „Jesteś 1258 m od placówki"
  // byłoby nieprawdą o miejscu, w którym pracownik dawno nie stoi, więc starsze
  // kliknięcie opisujemy w czasie przeszłym. Liczone raz przy montowaniu, żeby
  // nagłówek nie przeskoczył w trakcie wpisywania godziny.
  const [clickIsRecent] = useState(
    () => Date.now() - promptedAt.getTime() < RECENT_CLICK_MS,
  )
  const title =
    distanceLabel === null
      ? 'Kończysz pracę poza placówką'
      : clickIsRecent
        ? `Jesteś ${distanceLabel} od placówki`
        : `Koniec pracy kliknięto ${distanceLabel} od placówki`

  const [value, setValue] = useState(() => timeValue(promptedAt))
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dismissRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || resolve.isPending) return
      onDismiss()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onDismiss, resolve.isPending])

  /**
   * Kandydat w dobie KLIKNIĘCIA (`sameDay`) i wartość faktycznie wysyłana
   * (`corrected`, czyli `sameDay` cofnięty o dobę, gdy wypada po kliknięciu).
   *
   * Rozróżnienie jest potrzebne wyłącznie w komunikacie błędu. Przy starcie
   * 08:00 i kliknięciu 17:40 wpisane 19:00 cofa się na wczoraj i przez samo
   * porównanie z rozpoczęciem wyglądałoby na „godzinę sprzed startu", choć
   * pracownik miał na myśli późniejszą — a to jego jedyna ścieżka do zdania
   * „zgłoś to administratorowi".
   */
  function candidates(): { sameDay: Date; corrected: Date } | null {
    const parts = value.split(':')
    const h = Number(parts[0])
    const m = Number(parts[1])
    if (!Number.isInteger(h) || !Number.isInteger(m)) return null
    // Kotwicą jest doba KLIKNIĘCIA, nie rozpoczęcia: wpisana godzina zawsze
    // znaczy „ostatnie takie HH:MM przed kliknięciem". Dla sesji nocnej
    // (start 22:30, kliknięcie 01:15) wpisane 23:15 wypada dobę wcześniej,
    // czyli prawidłowo między startem a kliknięciem. Dla sesji dziennej
    // (start 08:00, kliknięcie 17:40) wpisane 06:00 zostaje dzisiejszym
    // porankiem — przed startem, więc pracownik czyta komunikat o DOLNEJ
    // granicy zamiast (przy kotwicy na starcie) mylącego „22h 0m pracy"
    // i komunikatu o granicy górnej.
    const sameDay = new Date(promptedAt)
    sameDay.setHours(h, m, 0, 0)
    const corrected = new Date(sameDay)
    if (sameDay.getTime() > promptedAt.getTime()) {
      corrected.setDate(corrected.getDate() - 1)
    }
    return { sameDay, corrected }
  }

  function correctedDate(): Date | null {
    return candidates()?.corrected ?? null
  }

  /** Błąd walidacji — pole da się poprawić, więc fokus wraca na nie. */
  function failValidation(message: string) {
    setError(message)
    inputRef.current?.focus()
  }

  async function submit(endedAt?: string) {
    setError(null)
    try {
      await resolve.mutateAsync({ sessionId: session.id, endedAt })
      toast.success('Dziękujemy — godzina wyjścia zapisana')
      onResolved()
    } catch (err) {
      const message = apiErrorMessage(err)
      toast.error(message)
      // Toast znika po kilku sekundach, a 409 (admin poprawił godzinę w
      // międzyczasie) jest nie do naprawienia z tego ekranu — treść zostaje
      // więc w modalu, a fokus idzie na jedyne wyjście, żeby pracownik nie
      // został z ekranem, którego nie da się przejść.
      setError(message)
      const unfixableHere = err instanceof ApiHttpError && err.status === 409
      // Fokus w makrozadaniu: w trakcie zapisu przyciski są disabled, a React
      // odmalowuje je jako aktywne dopiero po tym, jak mutacja odrzuci.
      setTimeout(() => {
        if (unfixableHere) dismissRef.current?.focus()
        else inputRef.current?.focus()
      }, 0)
    }
  }

  function handleSave() {
    // Wartość nietknięta = potwierdzenie, nie korekta. Pole nosi samo HH:MM,
    // więc zbudowana z niego data jest o kilkadziesiąt sekund wcześniejsza niż
    // remoteEndPromptAt (tam są sekundy) — wysłanie jej robiłoby z jednego
    // kliknięcia realną korektę: wpis w historii zmian i push do adminów
    // o zmianie „17:40 → 17:40".
    if (value === timeValue(promptedAt)) {
      void submit()
      return
    }

    const candidate = candidates()
    if (!candidate) {
      failValidation('Podaj godzinę w formacie GG:MM.')
      return
    }
    const { sameDay, corrected } = candidate
    const tooShort = corrected.getTime() - startedAt.getTime() < 60_000
    // Wpisana godzina nie mieści się w oknie sesji z żadnej strony: w dobie
    // kliknięcia wypada po nim, a dobę wcześniej przed rozpoczęciem. To zwykle
    // „pracowałem dłużej, niż zapisało kliknięcie" — jedyny przypadek, w którym
    // pracownik musi pójść do admina, więc komunikat mówi o granicy GÓRNEJ.
    if (sameDay.getTime() > promptedAt.getTime() && tooShort) {
      failValidation(
        `Godzina wyjścia nie może być późniejsza niż moment kliknięcia (${format(promptedAt, 'HH:mm')}). Jeśli praca trwała dłużej — zgłoś to administratorowi.`,
      )
      return
    }
    // Te same granice co w backendzie: minuta minimum i moment kliknięcia jako
    // sufit. Dolna łapie realne pomyłki (godzina sprzed startu), górna jest już
    // tylko zabezpieczeniem, gdyby ktoś kiedyś ruszył `candidates`.
    if (tooShort) {
      failValidation(
        `Godzina wyjścia musi być co najmniej minutę późniejsza niż rozpoczęcie (${format(startedAt, 'HH:mm')}).`,
      )
      return
    }
    if (corrected.getTime() > promptedAt.getTime()) {
      failValidation(
        `Godzina wyjścia nie może być późniejsza niż moment kliknięcia (${format(promptedAt, 'HH:mm')}). Jeśli praca trwała dłużej — zgłoś to administratorowi.`,
      )
      return
    }
    void submit(corrected.toISOString())
  }

  const workedMinutes = (() => {
    const corrected = correctedDate()
    if (!corrected) return null
    const minutes = Math.round((corrected.getTime() - startedAt.getTime()) / 60_000)
    return minutes > 0 ? minutes : null
  })()

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="remote-end-title"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-bg-canvas/85 backdrop-blur-sm p-4"
    >
      <div className="rounded-card border border-border-subtle bg-bg-elevated p-6 max-w-md w-full">
        <div className="flex items-start gap-3 mb-3">
          <MapPinOff className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" aria-hidden />
          <div>
            <h2 id="remote-end-title" className="text-lg font-semibold">
              {title}
            </h2>
            <p className="text-sm text-text-muted mt-1">
              {format(startedAt, 'EEEE, d MMMM', { locale: pl })} — praca od{' '}
              <span className="font-mono tabular-nums text-text-primary">
                {format(startedAt, 'HH:mm')}
              </span>
              . Kliknięcie „Koniec pracy" zapisaliśmy o{' '}
              <span className="font-mono tabular-nums text-text-primary">
                {format(promptedAt, 'HH:mm')}
              </span>
              .
            </p>
          </div>
        </div>

        <p className="text-sm text-text-muted mb-4">
          Podaj, proszę, realną godzinę zakończenia pracy — skorygujemy sesję
          i poinformujemy administratora.
        </p>

        <div className="mb-4">
          <Label htmlFor="remote-end-time">Godzina wyjścia</Label>
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              id="remote-end-time"
              type="time"
              value={value}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? ERROR_ID : undefined}
              onChange={(e) => {
                setValue(e.target.value)
                setError(null)
              }}
              className={cn(
                'h-12 rounded-action border bg-bg-elevated px-3 text-lg font-mono tabular-nums',
                'focus:outline-none focus:ring-2 focus:ring-brand-accent/40',
                error ? 'border-danger/60' : 'border-border-strong',
              )}
            />
            {workedMinutes !== null && (
              <span className="inline-flex items-center gap-1.5 text-sm text-text-muted">
                <Clock className="h-3.5 w-3.5" aria-hidden />
                {Math.floor(workedMinutes / 60)}h {workedMinutes % 60}m pracy
              </span>
            )}
          </div>
          {/* role="alert" ogłasza komunikat czytnikowi ekranu w momencie pojawienia. */}
          {error && (
            <p id={ERROR_ID} role="alert" className="mt-1.5 text-xs text-danger">
              {error}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Button type="button" onClick={handleSave} loading={resolve.isPending}>
            Zapisz godzinę wyjścia
          </Button>
          {/* Pytanie żyje 14 dni — po powrocie z banera w poniedziałek „koniec
              przed chwilą" byłby nieprawdą o piątkowej godzinie, więc przycisk
              nazywa konkretną wartość, którą potwierdza. */}
          <Button
            type="button"
            variant="secondary"
            disabled={resolve.isPending}
            onClick={() => void submit()}
          >
            Koniec pracy o {format(promptedAt, 'HH:mm')} — potwierdzam
          </Button>
          <button
            ref={dismissRef}
            type="button"
            onClick={onDismiss}
            disabled={resolve.isPending}
            className="text-xs text-text-faint hover:text-text-muted transition py-2 disabled:opacity-50"
          >
            Zrobię to później
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * 1 sesja czeka / 2–4 sesje czekają / 5+ sesji czeka. W polskim odmienia się
 * nie tylko rzeczownik, ale i imiesłów, i czasownik — stąd całe zdanie zamiast
 * sklejania rzeczownika z resztą.
 */
function reminderSentence(n: number): string {
  if (n === 1) return 'Jedna sesja zakończona poza placówką czeka na godzinę wyjścia.'
  const last = n % 10
  const lastTwo = n % 100
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) {
    return `${n} sesje zakończone poza placówką czekają na godzinę wyjścia.`
  }
  return `${n} sesji zakończonych poza placówką czeka na godzinę wyjścia.`
}

/**
 * Przypomnienie na ekranie głównym po odłożeniu modala — żeby zakończenie poza
 * placówką bez potwierdzonej godziny nie zniknęło pracownikowi z oczu.
 *
 * Treść leci `text-text-primary`, a `warning` zostaje ikonie i obramowaniu:
 * para warning-na-warning/10 daje w JASNYM motywie ok. 2,7:1 (AA wymaga 4.5:1),
 * choć w ciemnym wychodzi ok. 5,4:1 — usterka widoczna w jednym trybie z dwóch.
 */
export function RemoteEndReminder({
  count,
  onOpen,
}: {
  count: number
  onOpen: () => void
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left rounded-card border border-warning/40 bg-warning/10 px-4 py-3 flex items-center gap-3 hover:bg-warning/15 transition"
    >
      <MapPinOff className="h-4 w-4 text-warning flex-shrink-0" aria-hidden />
      <span className="text-sm text-text-primary">
        {reminderSentence(count)}{' '}
        <span className="underline underline-offset-2">Uzupełnij</span>
      </span>
    </button>
  )
}
